import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import type { U2000AutoCompleteSearchResult } from "../u2000Core/types/U2000AutoCompleteSearchResult";
import { u2000ExtractAutoCompleteResults } from "../u2000Core/utils/u2000ExtractAutoCompleteResults";
import { NS } from "./U3121";
import { doFindProjectManager, doFindCustomer, doFindLandSurveyor, doFindEmployee, doFindProject, luHandleAppiError, type U3121FindProjectItem } from "./U3121Service";
import {
  doCreateQuickCustomer,
  doFindCountries,
  doFindStates,
  doFindContactTypes,
  luHandleAppiError as u3120LuHandleAppiError,
  type U3120QuickCustomerCreatePayload,
} from "../u3120Customer/U3120Service";

const U3120_NS = "u3120";

const CRITERIA_TYPE = [] as { fieldName: string; value: unknown }[];

export type U3121FindSearchCriterias = typeof CRITERIA_TYPE;

/** Handler hook pour la recherche Chargé de projet (findProjectManager). Retourne search(criterias) => Promise<results>. */
export function useU3121FindProjectManagerHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3121FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<Record<string, unknown>>> => {
      try {
        const response = await doFindProjectManager(u2000AxiosAuthenticated, criterias);
        return u2000ExtractAutoCompleteResults<Record<string, unknown>>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [u2000AxiosAuthenticated, t, toast]
  );

  return { search };
}

/** Handler hook pour la recherche Projet (findProject). Retourne search(criterias) => Promise<results>. */
export function useU3121FindProjectHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3121FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<U3121FindProjectItem>> => {
      try {
        const response = await doFindProject(u2000AxiosAuthenticated, criterias);
        return u2000ExtractAutoCompleteResults<U3121FindProjectItem>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [u2000AxiosAuthenticated, t, toast],
  );

  return { search };
}

/** Handler hook pour la recherche Client (findCustomer). Retourne search(criterias) => Promise<results>. */
export function useU3121FindCustomerHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3121FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<Record<string, unknown>>> => {
      try {
        const response = await doFindCustomer(u2000AxiosAuthenticated, criterias);
        return u2000ExtractAutoCompleteResults<Record<string, unknown>>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [u2000AxiosAuthenticated, t, toast]
  );

  return { search };
}

/** Handler hook pour la recherche Arpenteur (findLandSurveyor). Retourne search(criterias) => Promise<results>. */
export function useU3121FindLandSurveyorHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3121FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<Record<string, unknown>>> => {
      try {
        const response = await doFindLandSurveyor(u2000AxiosAuthenticated, criterias);
        return u2000ExtractAutoCompleteResults<Record<string, unknown>>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [u2000AxiosAuthenticated, t, toast]
  );

  return { search };
}

/** Handler hook pour la recherche Pays (findCountries, U3120). Retourne search(criterias) => Promise<results>. */
export function useU3121FindCountriesHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3121FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<Record<string, unknown>>> => {
      try {
        const response = await doFindCountries(u2000AxiosAuthenticated, criterias);
        return u2000ExtractAutoCompleteResults<Record<string, unknown>>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [u2000AxiosAuthenticated, t, toast]
  );

  return { search };
}

/** Handler hook pour la recherche Province/État (findStates, U3120). Retourne search(criterias) => Promise<results>. */
export function useU3121FindStatesHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3121FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<Record<string, unknown>>> => {
      try {
        const response = await doFindStates(u2000AxiosAuthenticated, criterias);
        return u2000ExtractAutoCompleteResults<Record<string, unknown>>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [u2000AxiosAuthenticated, t, toast]
  );

  return { search };
}

export type U3121CreateQuickCustomerSuccess = { ok: true; created: unknown };
export type U3121CreateQuickCustomerFailure = { ok: false; fieldErrors?: Record<string, string> };
export type U3121CreateQuickCustomerResult = U3121CreateQuickCustomerSuccess | U3121CreateQuickCustomerFailure;

/** Handler hook pour la création rapide de client (U3120 createQuickCustomer). Ne pas appeler doCreateQuickCustomer depuis l’UI. */
export function useU3121CreateQuickCustomerHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const createQuickCustomer = useCallback(
    async (payload: U3120QuickCustomerCreatePayload): Promise<U3121CreateQuickCustomerResult> => {
      try {
        const response = await doCreateQuickCustomer(u2000AxiosAuthenticated, payload);
        const created = (response.data as { result?: unknown } | undefined)?.result ?? response.data;
        return { ok: true, created };
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, U3120_NS, u3120LuHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { ok: false, fieldErrors: errorResult.fieldErrors };
      }
    },
    [u2000AxiosAuthenticated, t, toast]
  );

  return { createQuickCustomer };
}

/** Handler hook pour la recherche Employé (findEmployee). Retourne search(criterias) => Promise<results>. Utilisé par Accès feuille de temps et Assignation employé. */
export function useU3121FindEmployeeHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3121FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<Record<string, unknown>>> => {
      try {
        const response = await doFindEmployee(u2000AxiosAuthenticated, criterias);
        return u2000ExtractAutoCompleteResults<Record<string, unknown>>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [u2000AxiosAuthenticated, t, toast]
  );

  return { search };
}

/** Handler hook pour la recherche Types de contact (findContactTypes, U3120). Utilisé par l'onglet Contact projet. */
export function useU3121FindContactTypesHandlers() {
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const search = useCallback(
    async (criterias: U3121FindSearchCriterias): Promise<U2000AutoCompleteSearchResult<Record<string, unknown>>> => {
      try {
        const response = await doFindContactTypes(u2000AxiosAuthenticated, criterias);
        return u2000ExtractAutoCompleteResults<Record<string, unknown>>(response.data);
      } catch (error: unknown) {
        const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
        return { results: [], totalCount: 0, truncated: false };
      }
    },
    [u2000AxiosAuthenticated, t, toast]
  );

  return { search };
}
